import { useState } from 'react';
import { Plus, Check, X, Pencil, EyeOff, Eye } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { colorDeCategoria, mensajeErrorGasto } from '../../utils/expenses';

/**
 * FE-12 — el catálogo de categorías de gasto, para el Administrador y, desde
 * FE-32 (ADR-030 §3), el Ejecutivo.
 *
 * Dos reglas del contrato se ven en esta pantalla, y las dos protegen el
 * histórico contable:
 *
 *  1. **No hay borrado.** Borrar una categoría reescribiría el significado de
 *     todos los gastos que ya la usan. Se desactiva: sale del formulario y los
 *     viejos siguen diciendo lo que decían.
 *  2. **`code` no se corrige.** Es el eje que los reportes referencian
 *     (ADR-016: el rollup agrupa por `code`). Cambiarlo partiría en dos la
 *     serie histórica de esa categoría.
 *
 * **La taxonomía es del tenant entero**, y por eso solo el Administrador la
 * toca: un Supervisor inventando categorías partiría en dos los reportes de
 * toda la empresa (`scope.go`, `ActionManageExpenseCatalog`).
 */
export function ExpenseCategoriesPanel({ search }: { search: string }) {
  const { categories, isLoading, error, crear, actualizar } = useExpenseCategories();

  const [creando, setCreando] = useState(false);
  const [nuevoCode, setNuevoCode] = useState('');
  const [nuevoLabel, setNuevoLabel] = useState('');
  const [nuevoScope, setNuevoScope] = useState<'VEHICLE' | 'OPERATION'>('VEHICLE');
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const visibles = categories
    .filter(c =>
      c.label.toLowerCase().includes(search.toLowerCase()) ||
      c.code.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => Number(b.active) - Number(a.active) || a.label.localeCompare(b.label));

  const crearCategoria = async () => {
    if (!nuevoCode.trim() || !nuevoLabel.trim()) {
      setAviso('El código y la etiqueta son obligatorios.');
      return;
    }
    setEnCurso(true);
    setAviso(null);
    try {
      await crear({ code: nuevoCode.trim(), label: nuevoLabel.trim(), scope: nuevoScope });
      setNuevoCode('');
      setNuevoLabel('');
      setCreando(false);
    } catch (err) {
      setAviso(mensajeErrorGasto(err, 'catalogo'));
    } finally {
      setEnCurso(false);
    }
  };

  const guardarEtiqueta = async (id: string) => {
    if (!editLabel.trim()) { setAviso('La etiqueta no puede quedar vacía.'); return; }
    setEnCurso(true);
    setAviso(null);
    try {
      await actualizar(id, { label: editLabel.trim() });
      setEditandoId(null);
    } catch (err) {
      setAviso(mensajeErrorGasto(err, 'catalogo'));
    } finally {
      setEnCurso(false);
    }
  };

  // Sin confirmación al desactivar: a diferencia de `NOTA` en las novedades,
  // ninguna categoría de gasto sostiene una funcionalidad por sí sola. Quedarse
  // sin categorías activas sí deja el formulario inservible, y de eso avisa el
  // propio formulario cuando pasa.
  const alternarActivo = async (id: string, active: boolean) => {
    setEnCurso(true);
    setAviso(null);
    try {
      await actualizar(id, { active: !active });
    } catch (err) {
      setAviso(mensajeErrorGasto(err, 'catalogo'));
    } finally {
      setEnCurso(false);
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>;
  }

  return (
    <div className="expenses-touch" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        Las categorías se <strong>desactivan</strong>, no se borran: un gasto viejo conserva la
        suya aunque hoy esté retirada. El <strong>código</strong> no se corrige — es el eje por el
        que agrupan los reportes. Las de <strong>operación</strong> (nómina, seguridad social…)
        cuentan en el resultado de la empresa pero no en el de cada vehículo.
      </p>

      {error && (
        <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444' }}>
          No se pudo cargar el catálogo: {error.message}
        </p>
      )}

      {aviso && (
        <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{aviso}</p>
      )}

      {creando ? (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
          <div style={{ flex: '0 1 180px' }}>
            <Input
              label="Código"
              value={nuevoCode}
              onChange={e => setNuevoCode(e.target.value)}
              placeholder="LAVADO"
            />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <Input
              label="Etiqueta"
              value={nuevoLabel}
              onChange={e => setNuevoLabel(e.target.value)}
              placeholder="Lavado"
            />
          </div>
          <div style={{ flex: '0 1 220px' }}>
            {/* FE-32 — el scope decide en qué resultado cuenta (ADR-030 §4) y no se corrige después. */}
            <Select
              label="Cuenta en"
              value={nuevoScope}
              onChange={e => setNuevoScope(e.target.value as 'VEHICLE' | 'OPERATION')}
              options={[{ value: 'VEHICLE', label: 'El vehículo (combustible, peajes…)' }, { value: 'OPERATION', label: 'La operación (nómina, seguridad social…)' }]}
            />
          </div>
          <Button size="sm" onClick={crearCategoria} isLoading={enCurso}>Crear</Button>
          <Button size="sm" variant="ghost" onClick={() => { setCreando(false); setAviso(null); }}>Cancelar</Button>
        </div>
      ) : (
        <Button size="sm" leftIcon={<Plus size={15} />} onClick={() => setCreando(true)} style={{ alignSelf: 'flex-start' }}>
          Nueva categoría
        </Button>
      )}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {visibles.map(t => {
          const color = colorDeCategoria(t.code);
          const editando = editandoId === t.id;
          return (
            <li
              key={t.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
                background: 'var(--bg-raised)', border: '1px solid var(--border)',
                borderLeft: `3px solid ${t.active ? color : 'var(--border-strong)'}`,
                borderRadius: 'var(--radius-md)', padding: '0.625rem 0.875rem',
                opacity: t.active ? 1 : 0.6,
              }}
            >
              <code style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', minWidth: '120px' }}>
                {t.code}
              </code>

              {editando ? (
                <>
                  <input
                    value={editLabel}
                    onChange={e => setEditLabel(e.target.value)}
                    style={{
                      flex: '1 1 160px', padding: '0.35rem 0.6rem', fontSize: '13px',
                      background: 'var(--bg-surface)', color: 'var(--text-primary)',
                      border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)',
                      fontFamily: 'inherit', outline: 'none',
                    }}
                  />
                  <Button size="sm" leftIcon={<Check size={13} />} onClick={() => guardarEtiqueta(t.id)} isLoading={enCurso}>
                    Guardar
                  </Button>
                  <Button size="sm" variant="ghost" leftIcon={<X size={13} />} onClick={() => setEditandoId(null)}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {t.label}
                    {t.scope === 'OPERATION' && <span title="Cuenta en el resultado del tenant y la sub-unidad, no en el del vehículo (ADR-030 §4)" style={{ marginLeft: '0.5rem', fontSize: '10px', fontWeight: 700, color: '#8B5CF6', background: 'rgba(139,92,246,0.12)', borderRadius: '99px', padding: '1px 7px' }}>operación</span>}
                    {!t.active && <span style={{ marginLeft: '0.5rem', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>(inactivo)</span>}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<Pencil size={13} />}
                    onClick={() => { setEditandoId(t.id); setEditLabel(t.label); setAviso(null); }}
                  >
                    Renombrar
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={t.active ? <EyeOff size={13} /> : <Eye size={13} />}
                    onClick={() => alternarActivo(t.id, t.active)}
                    disabled={enCurso}
                  >
                    {t.active ? 'Desactivar' : 'Reactivar'}
                  </Button>
                </>
              )}
            </li>
          );
        })}
      </ul>

      {visibles.length === 0 && (
        <p style={{ margin: 0, padding: '1rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
          {search ? 'Ninguna categoría coincide con la búsqueda.' : 'El catálogo está vacío.'}
        </p>
      )}
    </div>
  );
}
